import React, { useEffect, useRef, useLayoutEffect } from 'react';
import ReactDOM from 'react-dom';

export interface InfoPopoverProps {
  anchorEl: HTMLElement | null;
  title: string;
  children: React.ReactNode;
  onClose: () => void;
  minWidth?: number;
  maxWidth?: number;
  placement?: 'top' | 'bottom' | 'auto'; // Add placement option
}

const InfoPopover: React.FC<InfoPopoverProps> = ({ 
  anchorEl, 
  title, 
  children, 
  onClose, 
  minWidth = 220, 
  maxWidth = 340, 
  placement = 'auto' // Default to auto placement
}) => {
  const popoverRef = useRef<HTMLDivElement>(null);
  const [style, setStyle] = React.useState<React.CSSProperties>({ top: 0, left: 0, position: 'absolute', zIndex: 9999 });

  useLayoutEffect(() => {
    if (anchorEl && popoverRef.current) {
      const anchorRect = anchorEl.getBoundingClientRect();
      const popoverRect = popoverRef.current.getBoundingClientRect();
      
      let top;
      // Apply placement based on prop
      if (placement === 'top') {
        // Always place above the element
        top = anchorRect.top + window.scrollY - popoverRect.height - 8;
      } else if (placement === 'bottom') {
        // Always place below the element
        top = anchorRect.bottom + window.scrollY + 8;
      } else {
        // Auto placement (default): Try to position below, fallback to above if not enough space
        top = anchorRect.bottom + window.scrollY + 8;
        if (top + popoverRect.height > window.innerHeight) {
          top = anchorRect.top + window.scrollY - popoverRect.height - 8;
        }
      }
      
      // Horizontal positioning remains the same
      let left = anchorRect.left + window.scrollX;
      if (left + popoverRect.width > window.innerWidth) {
        left = window.innerWidth - popoverRect.width - 8;
      }
      setStyle(s => ({ ...s, top, left }));
    }
  }, [anchorEl, children, placement]);

  // Close on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node) && anchorEl && !anchorEl.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [onClose, anchorEl]);

  if (!anchorEl) return null;
  return ReactDOM.createPortal(
    <div ref={popoverRef} className="info-popover" style={{
      ...style,
      minWidth,
      maxWidth,
      background: 'var(--color-bg-secondary, #1a2638)',
      border: '1px solid var(--color-border-light, #334466)',
      borderRadius: 8,
      boxShadow: '0 2px 12px rgba(0,0,0,0.25)',
      color: 'var(--color-text-bright, #fff)',
      padding: 0,
      zIndex: 9999
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.75rem 1rem 0.25rem 1rem', borderBottom: '1px solid #334466', fontWeight: 700 }}>
        <span>{title}</span>
        <button
          onClick={onClose}
          style={{
            background: 'var(--color-bg-secondary, #1a2638)',
            border: '1px solid var(--color-border-light, #334466)',
            borderRadius: '50%',
            width: 24,
            height: 24,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#2196f3',
            fontSize: 18,
            cursor: 'pointer',
            marginLeft: 8,
            boxShadow: '0 1px 2px rgba(0,0,0,0.10)',
            padding: 0,
            outline: 'none',
            transition: 'background 0.2s, color 0.2s',
            lineHeight: 1,
            position: 'relative',
          }}
          aria-label="Close info popover"
        >
          <span style={{ fontWeight: 'bold', fontSize: 18, lineHeight: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', position: 'absolute', top: '50%', left: '50%', transform: 'translate(-52%, -57%)' }}>&times;</span>
        </button>
      </div>
      <div style={{ padding: '0.75rem 1rem 1rem 1rem' }}>{children}</div>
    </div>,
    document.body
  );
};

export default InfoPopover;
